import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useParams, useNavigate } from "react-router-dom";
import { addMonths, differenceInDays } from "date-fns";
import { patientsService } from "../services/patientsService";
import { supabase } from "../services/supabaseClient";
import {
  templatesService,
  ClinicalTemplate,
} from "../services/templatesService";
import {
  TrendingUp,
  Plus,
  ClipboardList,
  History,
  ChevronDown,
  Mic,
  Sparkles,
  Loader2,
  Trash2,
  Paperclip,
  Upload,
  Clock,
  UserCheck,
  Pencil,
} from "lucide-react";
import { Button } from "../components/ui";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { useAuth } from "../context/AuthContext";
import { CustomSelect } from "../components/CustomSelect";
import Swal from "sweetalert2";
import AudioRecorderWidget from "../components/AudioRecorderWidget";
import { EditEvolutionModal } from "../components/EditEvolutionModal";

// --- Styled Components (Dashboard First) ---

const Container = styled.div`
  max-width: 1560px;
  margin: 0 auto;
  padding: 2rem 2.5rem;
  color: #f8fafc;
  min-height: 100vh;

  @media (max-width: 768px) {
    padding: 1.25rem 1rem;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2.25rem;

  h1 {
    font-size: clamp(1.5rem, 3vw, 2.25rem);
    font-weight: 800;
    background: linear-gradient(135deg, #ffffff 0%, #cbd5e1 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    display: flex;
    align-items: center;
    gap: 0.85rem;
  }
`;

const HashBadge = styled.span`
  background: rgba(16, 185, 129, 0.15);
  color: #34d399;
  border: 1px solid rgba(16, 185, 129, 0.3);
  font-family: monospace;
  padding: 0.35rem 0.85rem;
  border-radius: 0.75rem;
  font-size: 0.9rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 1.75rem;
`;

const InfoBox = styled.div`
  background: rgba(17, 24, 39, 0.7);
  backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-top: 4px solid ${(props) => props.color || "rgba(16, 185, 129, 0.5)"};
  border-radius: 1.5rem;
  position: relative;
  overflow: hidden;
  color: #f8fafc;
`;

const Card = styled.div<{ color?: string }>`
  background: rgba(17, 24, 39, 0.7);
  backdrop-filter: blur(16px);
  border-radius: 1.5rem;
  padding: 1.75rem;
  box-shadow: 0 10px 30px -5px rgba(0, 0, 0, 0.35);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-top: 4px solid ${(props) => props.color || "rgba(16, 185, 129, 0.5)"};
  position: relative;
  overflow: hidden;
  color: #f8fafc;
`;

const CardTitle = styled.h3`
  font-size: 1.1rem;
  color: #f8fafc;
  margin: 0 0 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

// --- Custom Components ---

const ChromaticSlider = styled.input`
  width: 100%;
  -webkit-appearance: none;
  height: 8px;
  border-radius: 4px;
  background: linear-gradient(to right, #48bb78, #ecc94b, #f56565);
  outline: none;
  margin: 1rem 0;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: white;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
    cursor: pointer;
    border: 2px solid #2d3748;
  }
`;

const MetricBox = styled.div`
  margin-bottom: 1.5rem;
  p {
    margin: 0;
    font-size: 0.9rem;
    color: #94a3b8;
  }
  .value {
    font-size: 1.5rem;
    font-weight: bold;
    color: #f8fafc;
  }
`;

const calculateAge = (dob: string | undefined) => {
  if (!dob) return "-";
  const diff = Date.now() - new Date(dob).getTime();
  if (isNaN(diff)) return "-";
  const age = new Date(diff);
  return Math.abs(age.getUTCFullYear() - 1970);
};

const PatientDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  // Fetch Org ID from local storage as per application patterns (fallback to empty)
  const selectedOrgId = localStorage.getItem("selectedOrgId") || "";
  const [loading, setLoading] = useState(true);
  const [patient, setPatient] = useState<any | null>(null);
  const [admission, setAdmission] = useState<any | null>(null);
  const [evolutions, setEvolutions] = useState<any[]>([]);
  const [templates, setTemplates] = useState<ClinicalTemplate[]>([]);

  // UI State for Accordion
  const [expandedEvos, setExpandedEvos] = useState<number[]>([]);
  const [transcribingEvoId, setTranscribingEvoId] = useState<string | null>(null);

  // Edit Evolution State
  const [editingEvolution, setEditingEvolution] = useState<any | null>(null);
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  // New Evolution State
  const [isEvolutionModalOpen, setIsEvolutionModalOpen] = useState(false);

  const [isUploadingEvolution, setIsUploadingEvolution] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const [newEvolution, setNewEvolution] = useState({
    title: "",
    date: new Date().toISOString().split("T")[0],
    eva_score: 0,
    notes: "",
    sparing_effect: [] as any[],
    adverse_effects: [] as any[],
    template_id: null as string | null,
    template_data: {} as Record<string, any>,
    files: [] as File[],
    next_follow_up_months: 6, // DEFAULT: 6 months
    audio_url: null as string | null,
  });

  // Clinical Evolution Draft Persistence
  const draftKey = id ? `draft_evolution_${id}_${user?.id || 'default'}` : null;
  const [draftRestoredAt, setDraftRestoredAt] = useState<number | null>(null);
  const [draftSavedAt, setDraftSavedAt] = useState<number | null>(null);

  // Restore draft when opening evolution modal
  useEffect(() => {
    if (isEvolutionModalOpen && draftKey) {
      try {
        const raw = localStorage.getItem(draftKey);
        if (raw) {
          const parsed = JSON.parse(raw);
          const hasContent =
            (parsed.title && parsed.title.trim().length > 0) ||
            (parsed.notes && parsed.notes.trim().length > 0) ||
            (parsed.template_data && Object.keys(parsed.template_data).length > 0) ||
            (parsed.eva_score && parsed.eva_score > 0);

          if (hasContent) {
            setNewEvolution((prev) => ({
              ...prev,
              title: parsed.title || prev.title,
              date: parsed.date || prev.date,
              eva_score: parsed.eva_score ?? prev.eva_score,
              notes: parsed.notes || prev.notes,
              sparing_effect: parsed.sparing_effect || prev.sparing_effect,
              adverse_effects: parsed.adverse_effects || prev.adverse_effects,
              template_id: parsed.template_id || prev.template_id,
              template_data: parsed.template_data || prev.template_data,
              next_follow_up_months: parsed.next_follow_up_months ?? prev.next_follow_up_months,
              audio_url: parsed.audio_url || prev.audio_url,
              files: [],
            }));
            setDraftRestoredAt(parsed.savedAt || Date.now());
          }
        }
      } catch (err) {
        console.warn('Error reading clinical evolution draft:', err);
      }
    }
  }, [isEvolutionModalOpen, draftKey]);

  // Debounced autosave evolution draft to localStorage
  useEffect(() => {
    if (!isEvolutionModalOpen || !draftKey) return;

    const hasContent =
      newEvolution.notes.trim().length > 0 ||
      newEvolution.title.trim().length > 0 ||
      (newEvolution.template_data && Object.keys(newEvolution.template_data).length > 0) ||
      newEvolution.eva_score > 0;

    const timer = setTimeout(() => {
      try {
        if (hasContent) {
          const payload = {
            title: newEvolution.title,
            date: newEvolution.date,
            eva_score: newEvolution.eva_score,
            notes: newEvolution.notes,
            sparing_effect: newEvolution.sparing_effect,
            adverse_effects: newEvolution.adverse_effects,
            template_id: newEvolution.template_id,
            template_data: newEvolution.template_data,
            next_follow_up_months: newEvolution.next_follow_up_months,
            audio_url: newEvolution.audio_url,
            savedAt: Date.now(),
          };
          localStorage.setItem(draftKey, JSON.stringify(payload));
          setDraftSavedAt(Date.now());
        }
      } catch (err) {
        console.warn('Error saving clinical evolution draft:', err);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [
    isEvolutionModalOpen,
    draftKey,
    newEvolution.title,
    newEvolution.date,
    newEvolution.eva_score,
    newEvolution.notes,
    newEvolution.sparing_effect,
    newEvolution.adverse_effects,
    newEvolution.template_id,
    newEvolution.template_data,
    newEvolution.next_follow_up_months,
    newEvolution.audio_url,
  ]);

  // Prevent accidental tab closure or navigation while writing
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      const hasUnsavedContent =
        isEvolutionModalOpen &&
        (newEvolution.notes.trim().length > 0 || newEvolution.title.trim().length > 0);

      if (hasUnsavedContent) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isEvolutionModalOpen, newEvolution.notes, newEvolution.title]);

  const handleDiscardDraft = () => {
    if (draftKey) {
      localStorage.removeItem(draftKey);
    }
    setDraftRestoredAt(null);
    setDraftSavedAt(null);
    setNewEvolution({
      title: "",
      date: new Date().toISOString().split("T")[0],
      eva_score: 0,
      notes: "",
      sparing_effect: [],
      adverse_effects: [],
      template_id: null,
      template_data: {},
      files: [],
      next_follow_up_months: 6,
      audio_url: null,
    });
  };

  // Admission Form State
  const [isAdmitting, setIsAdmitting] = useState(false);
  const [newAdmission, setNewAdmission] = useState({
    baseline_qol: 50,
    baseline_pain_avg: 5,
    baseline_pain_worst: 8,
    diagnosis_cie11: [] as string[],
    template_id: null as string | null,
    template_data: {} as Record<string, any>,
  });

  useEffect(() => {
    if (id) loadData(id);

    async function fetchTemplates() {
      const data = await templatesService.getTemplates();
      setTemplates(data);
    }
    fetchTemplates();
  }, [id]);

  const loadData = async (patientId: string) => {
    setLoading(true);
    try {
      const pat = await patientsService.getPatientById(patientId);
      setPatient(pat);

      const adm = await patientsService.getClinicalAdmission(patientId);
      setAdmission(adm);

      if (adm) {
        const evos = await patientsService.getEvolutions(adm.id);
        setEvolutions(evos);
      }
    } catch (err: any) {
      console.error(err);
      Swal.fire(
        "Error",
        "Ocurrió un error al cargar los datos del paciente.",
        "error"
      );
    } finally {
      setLoading(false);

      // Handle automatic opening from the "Seguimiento" button
      const params = new URLSearchParams(window.location.search);
      if (params.get("action") === "new_followup") {
        setTimeout(() => setIsEvolutionModalOpen(true), 100);
      }
    }
  };

  const handleCreateAdmission = async () => {
    if (!id) return;
    try {
      // Generate hash manually for display
      const cleanId = id.replace(/-/g, "").substring(0, 8).toUpperCase();
      const hash = `ANON-${cleanId}`;

      // Obtener el valor real del EVA desde la plantilla, si existe.
      let baselineEva = 0;
      if (newAdmission.template_id) {
        const template = templates.find((t: any) => t.id === newAdmission.template_id);
        const evaField = template?.fields?.find((f: any) => f.type === 'eva');
        if (evaField && newAdmission.template_data[evaField.id] !== undefined) {
          baselineEva = Number(newAdmission.template_data[evaField.id]);
        }
      }

      const createdAdmission = await patientsService.createClinicalAdmission({
        patient_id: id,
        baseline_qol: 0,
        baseline_pain_avg: baselineEva,
        baseline_pain_worst: 0,
        diagnosis_cie11: [],
        patient_hash: hash, // Override with generated hash
      });

      if (createdAdmission && newAdmission.template_id) {
        await patientsService.addEvolution({
          admission_id: createdAdmission.id,
          title: "Admisión Inicial (Línea Base)",
          date: new Date().toISOString().split("T")[0],
          eva_score: baselineEva,
          improvement_percent: 0,
          notes: "Línea base (Admisión Clínica)",
          sparing_effect: [],
          adverse_effects: [],
          template_id: newAdmission.template_id,
          template_data: newAdmission.template_data,
          next_follow_up_months: 6,
        });
      }

      loadData(id);
      setIsAdmitting(false);
    } catch (e) {
      alert("Error creating admission");
    }
  };

  const handleTranscribeAudio = async (evoId: string, audioUrl: string) => {
    try {
      setTranscribingEvoId(evoId);

      const { data, error } = await supabase.functions.invoke('process-clinical-audio', {
        body: { evolution_id: evoId, audio_url: audioUrl }
      });

      if (error) throw error;

      if (data?.success) {
        Swal.fire({
          title: 'Transcripción Exitosa',
          text: 'La Inteligencia Artificial ha extraído el legajo.',
          icon: 'success',
          background: "rgba(30, 41, 59, 0.95)",
          color: "#f8fafc",
          customClass: { popup: 'glass-modal border border-white/10 rounded-xl' }
        });
        loadData(id || ""); // Refresh to show the ai_transcript
      } else {
        throw new Error(data?.error || "Error desconocido en el servidor de IA");
      }
    } catch (err: any) {
      console.error("Transcribe Error:", err);
      Swal.fire({
        title: 'Fallo de IA',
        text: err.message || 'No se pudo procesar el audio en este momento.',
        icon: 'error',
        background: "rgba(30, 41, 59, 0.95)",
        color: "#f8fafc",
        customClass: { popup: 'glass-modal border border-white/10 rounded-xl' }
      });
    } finally {
      setTranscribingEvoId(null);
    }
  };

  const handleAddEvolution = async () => {
    if (!admission) return;
    try {
      setIsUploadingEvolution(true);
      let actualEvaScore = newEvolution.eva_score;

      // Si el EVA fue cargado a través de una Plantilla Dinámica, sobreescribir el score nativo.
      if (newEvolution.template_id) {
        const template = templates.find(
          (t: any) => t.id === newEvolution.template_id,
        );
        const evaField = template?.fields?.find((f: any) => f.type === "eva");
        if (evaField && newEvolution.template_data[evaField.id] !== undefined) {
          actualEvaScore = Number(newEvolution.template_data[evaField.id]);
        }
      }

      let previousEva = admission.baseline_pain_avg;

      // Buscar el EVA de la evolución INMEDIATAMENTE ANTERIOR (la última registrada)
      if (evolutions.length > 0) {
        // Como 'evolutions' está ordenado de más reciente a más antigua, el índice 0 es la última visita.
        previousEva = evolutions[0].eva_score;
      }

      let improvement = 0;
      if (previousEva > 0) {
        improvement = ((previousEva - actualEvaScore) / previousEva) * 100;
      } else if (previousEva === 0 && actualEvaScore > 0) {
        // Empezó la sesión anterior sin dolor y ahora tiene dolor, empeora al 100% (negativo)
        improvement = -100;
      }

      // Upload files
      const uploadedUrls: string[] = [];
      for (const file of newEvolution.files) {
        const path = `evolutions/${id}/${Date.now()}_${file.name}`;
        const url = await patientsService.uploadDocument(file, path);
        if (url) uploadedUrls.push(url);
      }

      await patientsService.addEvolution({
        admission_id: admission.id,
        title: newEvolution.title,
        date: newEvolution.date,
        eva_score: actualEvaScore,
        improvement_percent: parseFloat(improvement.toFixed(2)),
        notes: newEvolution.notes,
        sparing_effect: newEvolution.sparing_effect,
        adverse_effects: newEvolution.adverse_effects,
        template_id: newEvolution.template_id,
        template_data: newEvolution.template_data,
        attachments: uploadedUrls,
        next_follow_up_months: newEvolution.next_follow_up_months,
        audio_url: newEvolution.audio_url,
      });

      loadData(id || "");
      if (draftKey) {
        localStorage.removeItem(draftKey);
      }
      setDraftRestoredAt(null);
      setDraftSavedAt(null);
      setIsEvolutionModalOpen(false);
      setNewEvolution({
        title: "",
        date: new Date().toISOString().split("T")[0],
        eva_score: 0,
        notes: "",
        sparing_effect: [],
        adverse_effects: [],
        template_id: null,
        template_data: {},
        files: [],
        next_follow_up_months: 6,
        audio_url: null,
      }); // Reset
    } catch (e: any) {
      console.error("Error adding evolution:", e);
      const errorMessage = e?.message || e?.error_description || (typeof e === "string" ? e : "");

      Swal.fire({
        title: "Error al guardar la evolución",
        html: `
          <p class="mb-2">No se pudo registrar la nota de evolución.</p>
          ${errorMessage ? `<p class="text-xs text-red-300 font-mono bg-red-950/50 p-2.5 rounded border border-red-500/20 mb-3 text-left overflow-auto max-h-24">${errorMessage}</p>` : ""}
          <p class="text-xs text-slate-300 bg-slate-800/80 p-2 rounded border border-slate-700 text-left">💡 <b>Sugerencia:</b> Si tu sesión estuvo inactiva o hubo un corte de conexión, por favor <b>refrescá la página (F5)</b> e intentá guardar nuevamente.</p>
        `,
        icon: "error",
        background: "rgba(30, 41, 59, 0.95)",
        color: "#f8fafc",
        confirmButtonColor: "#3b82f6",
        customClass: { popup: "glass-modal border border-white/10 rounded-xl" }
      });
    } finally {
      setIsUploadingEvolution(false);
    }
  };

  const handleSaveEditedEvolution = async (
    payload: any,
    newFiles: File[],
    retainedAttachments: string[]
  ) => {
    if (!editingEvolution) return;

    try {
      setIsSavingEdit(true);

      // 1. Upload any newly added files
      const uploadedUrls: string[] = [];
      for (const file of newFiles) {
        const path = `evolutions/${id}/${Date.now()}_${file.name}`;
        const url = await patientsService.uploadDocument(file, path);
        if (url) uploadedUrls.push(url);
      }

      const finalAttachments = [...retainedAttachments, ...uploadedUrls];

      // 2. Recalculate improvement percentage if EVA score was changed
      let actualEvaScore = payload.eva_score;
      let previousEva = admission?.baseline_pain_avg || 0;

      // Find previous evolution in sequence (the one created immediately prior to this one)
      const currentIndex = evolutions.findIndex((e) => e.id === editingEvolution.id);
      if (currentIndex !== -1 && currentIndex < evolutions.length - 1) {
        previousEva = evolutions[currentIndex + 1].eva_score;
      }

      let improvement = 0;
      if (previousEva > 0) {
        improvement = ((previousEva - actualEvaScore) / previousEva) * 100;
      } else if (previousEva === 0 && actualEvaScore > 0) {
        improvement = -100;
      }

      // 3. Prepare Audit Stamp & History
      const editorName = user?.name
        ? user.role === "medico"
          ? `Dr. ${user.name}`
          : user.name
        : user?.email || "Profesional / Usuario";

      const nowIso = new Date().toISOString();

      const previousHistory =
        editingEvolution.edit_history ||
        editingEvolution.template_data?._audit?.edit_history ||
        [];

      const newHistoryEntry = {
        edited_at: nowIso,
        edited_by: editorName,
        reason: payload.edit_reason || "Modificación de detalles clínicos",
        previous_eva: editingEvolution.eva_score,
        new_eva: actualEvaScore,
      };

      const finalPayload = {
        title: payload.title,
        date: payload.date,
        eva_score: actualEvaScore,
        improvement_percent: parseFloat(improvement.toFixed(2)),
        notes: payload.notes,
        next_follow_up_months: payload.next_follow_up_months,
        template_id: payload.template_id,
        template_data: payload.template_data,
        attachments: finalAttachments,
        updated_at: nowIso,
        updated_by: editorName,
        edit_history: [...previousHistory, newHistoryEntry],
      };

      await patientsService.updateEvolution(editingEvolution.id, finalPayload);

      Swal.fire({
        title: "Evolución Actualizada",
        text: "Los cambios y el sello de auditoría han sido registrados exitosamente.",
        icon: "success",
        background: "rgba(30, 41, 59, 0.95)",
        color: "#f8fafc",
        confirmButtonColor: "#10b981",
        customClass: { popup: "glass-modal border border-white/10 rounded-xl" },
      });

      setEditingEvolution(null);
      loadData(id || "");
    } catch (err: any) {
      console.error("Error saving edited evolution:", err);
      Swal.fire({
        title: "Error al actualizar",
        text: err?.message || "No se pudo actualizar la evolución clínica.",
        icon: "error",
        background: "rgba(30, 41, 59, 0.95)",
        color: "#f8fafc",
        confirmButtonColor: "#3b82f6",
        customClass: { popup: "glass-modal border border-white/10 rounded-xl" },
      });
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleDrag = (e: React.DragEvent) => {

    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setIsDragActive(true);
    } else if (e.type === "dragleave") {
      setIsDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      setNewEvolution((prev) => ({
        ...prev,
        files: [
          ...prev.files,
          ...Array.from(e.dataTransfer.files),
        ],
      }));
    }
  };

  if (loading) return <LoadingSpinner />;

  const lastVisitDate =
    evolutions.length > 0
      ? evolutions[0].date
      : admission
        ? new Date(admission.created_at).toLocaleDateString()
        : "-";
  const pathologyText =
    patient?.pathology ||
    (admission?.diagnosis_cie11?.length
      ? admission.diagnosis_cie11.join(", ")
      : "-");

  const ProfileHeaderContent = (
    <Header>
      <div>
        <h1>
          Perfil del Paciente
          {patient?.profile?.full_name ? ` - ${patient.profile.full_name}` : patient?.name ? ` - ${patient.name}` : ""}
        </h1>
        <div
          style={{
            display: "flex",
            gap: "1.5rem",
            marginTop: "0.75rem",
            color: "#94a3b8",
            fontSize: "0.95rem",
            flexWrap: "wrap",
          }}
        >
          <span>
            <strong>DNI:</strong> {patient?.document_number || "-"}
          </span>
          <span>
            <strong>Edad:</strong> {calculateAge(patient?.date_of_birth)}
          </span>
          <span>
            <strong>Patología:</strong> {pathologyText}
          </span>
          <span>
            <strong>Últ. Consulta:</strong> {lastVisitDate}
          </span>
          <span>
            <strong>REPROCANN: </strong>
            <span
              style={{
                background: patient?.reprocann_number
                  ? "rgba(74, 222, 128, 0.2)"
                  : "rgba(239, 68, 68, 0.2)",
                color: patient?.reprocann_number ? "#4ade80" : "#f87171",
                border: `1px solid ${patient?.reprocann_number ? "rgba(74, 222, 128, 0.5)" : "rgba(239, 68, 68, 0.5)"}`,
                padding: "0.2rem 0.5rem",
                borderRadius: "0.25rem",
                fontWeight: "bold",
                fontSize: "0.85rem",
              }}
            >
              {patient?.reprocann_number || "Sin Vincular"}
            </span>
          </span>
        </div>
      </div>
      {admission && (
        <HashBadge>
          <UserCheck size={14} /> {admission.patient_hash}
        </HashBadge>
      )}
    </Header>
  );

  if (!admission && !isAdmitting) {
    return (
      <Container>
        {ProfileHeaderContent}

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "60vh",
            flexDirection: "column",
            background: "rgba(30, 41, 59, 0.6)",
            borderRadius: "1rem",
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.2)",
            border: "1px solid rgba(255, 255, 255, 0.05)",
            borderTop: "4px solid rgba(255, 255, 255, 0.1)",
            backdropFilter: "blur(12px)",
          }}
        >
          <UserCheck size={64} className="text-slate-500" strokeWidth={1.5} />
          <h2 style={{ color: "#f8fafc", marginTop: "1.5rem" }}>
            Sin Datos Clínicos
          </h2>
          <p
            style={{
              color: "#718096",
              maxWidth: "400px",
              textAlign: "center",
              marginBottom: "2rem",
            }}
          >
            Este paciente aún no tiene una Admisión Clínica registrada
            (Baseline).
          </p>
          <Button
            size="lg"
            variant="default"
            onClick={() => setIsAdmitting(true)}
            className="flex items-center gap-2"
          >
            <Plus size={18} /> Iniciar Admisión Clínica
          </Button>
        </div>
      </Container>
    );
  }

  // --- ADMISSION FORM VIEW ---
  if (isAdmitting) {
    return (
      <Container>
        {ProfileHeaderContent}
        <Card color="#319795" style={{ maxWidth: "800px", margin: "0 auto" }}>
          <CardTitle>Línea Base (Baseline Metrics)</CardTitle>

          {/* Template Selector Section */}
          <div
            style={{
              background: "rgba(30, 41, 59, 0.4)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              padding: "1rem",
              borderRadius: "0.5rem",
              marginBottom: "1.5rem",
            }}
          >
            <label
              style={{
                marginBottom: "0.5rem",
                fontWeight: "bold",
                color: "#f8fafc",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
              }}
            >
              <ClipboardList size={16} className="text-emerald-400" /> Seleccionar Plantilla Clínica (Obligatorio)
            </label>
            <select
              style={{
                width: "100%",
                padding: "0.75rem",
                borderRadius: "0.5rem",
                border: "1px solid rgba(255,255,255,0.2)",
                fontFamily: "inherit",
                background: "rgba(15, 23, 42, 0.8)",
                color: "white",
              }}
              value={newAdmission.template_id || ""}
              onChange={(e) => {
                const tId = e.target.value;
                if (!tId) {
                  setNewAdmission({
                    ...newAdmission,
                    template_id: null,
                    template_data: {},
                  });
                  return;
                }
                const selected = templates.find((t: any) => t.id === tId);
                const defaultData: Record<string, any> = {};
                selected?.fields.forEach((f: any) => {
                  if (f.type === "checkbox") defaultData[f.id] = [];
                  else defaultData[f.id] = "";
                });
                setNewAdmission({
                  ...newAdmission,
                  template_id: tId,
                  template_data: defaultData,
                });
              }}
            >
              <option value="">Seleccione una plantilla...</option>
              {templates.map((t: any) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>

          {/* Dynamics fields block */}
          {newAdmission.template_id && (
            <div
              style={{
                background: "rgba(30, 41, 59, 0.4)",
                padding: "1rem",
                borderRadius: "0.5rem",
                marginBottom: "1.5rem",
                border: "1px solid rgba(255, 255, 255, 0.1)",
              }}
            >
              <h4 style={{ margin: "0 0 1rem 0", color: "#f8fafc" }}>
                Completar Datos de la Plantilla
              </h4>
              {templates
                .find((t: any) => t.id === newAdmission.template_id)
                ?.fields.map((field: any) => (
                  <div key={field.id} style={{ marginBottom: "1rem" }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "0.5rem",
                        fontWeight: "600",
                        color: "#cbd5e1",
                        fontSize: "0.9rem",
                      }}
                    >
                      {field.label}{" "}
                      {field.required && (
                        <span style={{ color: "#ef4444" }}>*</span>
                      )}
                    </label>

                    {field.type === "text" && (
                      <input
                        type="text"
                        style={{
                          width: "100%",
                          padding: "0.6rem",
                          borderRadius: "0.375rem",
                          border: "1px solid rgba(255,255,255,0.2)",
                          background: "rgba(15, 23, 42, 0.5)",
                          color: "white",
                        }}
                        value={newAdmission.template_data[field.id] || ""}
                        onChange={(e) =>
                          setNewAdmission({
                            ...newAdmission,
                            template_data: {
                              ...newAdmission.template_data,
                              [field.id]: e.target.value,
                            },
                          })
                        }
                        required={field.required}
                      />
                    )}
                    {field.type === "textarea" && (
                      <textarea
                        style={{
                          width: "100%",
                          padding: "0.6rem",
                          borderRadius: "0.375rem",
                          border: "1px solid rgba(255,255,255,0.2)",
                          background: "rgba(15, 23, 42, 0.5)",
                          color: "white",
                          minHeight: "80px",
                        }}
                        value={newAdmission.template_data[field.id] || ""}
                        onChange={(e) =>
                          setNewAdmission({
                            ...newAdmission,
                            template_data: {
                              ...newAdmission.template_data,
                              [field.id]: e.target.value,
                            },
                          })
                        }
                        required={field.required}
                      />
                    )}
                    {field.type === "date" && (
                      <input
                        type="date"
                        style={{
                          width: "100%",
                          padding: "0.6rem",
                          borderRadius: "0.375rem",
                          border: "1px solid rgba(255,255,255,0.2)",
                          background: "rgba(15, 23, 42, 0.5)",
                          color: "white",
                        }}
                        value={newAdmission.template_data[field.id] || ""}
                        onChange={(e) =>
                          setNewAdmission({
                            ...newAdmission,
                            template_data: {
                              ...newAdmission.template_data,
                              [field.id]: e.target.value,
                            },
                          })
                        }
                        required={field.required}
                      />
                    )}
                    {field.type === "select" && (
                      <select
                        style={{
                          width: "100%",
                          padding: "0.6rem",
                          borderRadius: "0.375rem",
                          border: "1px solid rgba(255,255,255,0.2)",
                          background: "rgba(15, 23, 42, 0.9)",
                          color: "white",
                        }}
                        value={newAdmission.template_data[field.id] || ""}
                        onChange={(e) =>
                          setNewAdmission({
                            ...newAdmission,
                            template_data: {
                              ...newAdmission.template_data,
                              [field.id]: e.target.value,
                            },
                          })
                        }
                        required={field.required}
                      >
                        <option value="">Seleccione...</option>
                        {field.options?.map((opt: string) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                    )}
                    {field.type === "checkbox" && (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "0.5rem",
                          background: "rgba(15, 23, 42, 0.5)",
                          padding: "0.5rem",
                          borderRadius: "0.375rem",
                          border: "1px solid rgba(255,255,255,0.2)",
                          color: "lightgray",
                        }}
                      >
                        {field.options?.map((opt: string) => {
                          const isChecked = (
                            newAdmission.template_data[field.id] || []
                          ).includes(opt);
                          return (
                            <label
                              key={opt}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "0.5rem",
                                fontSize: "0.9rem",
                                cursor: "pointer",
                                margin: 0,
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={(e) => {
                                  const current = [
                                    ...(newAdmission.template_data[field.id] ||
                                      []),
                                  ];
                                  if (e.target.checked) current.push(opt);
                                  else {
                                    const idx = current.indexOf(opt);
                                    if (idx > -1) current.splice(idx, 1);
                                  }
                                  setNewAdmission({
                                    ...newAdmission,
                                    template_data: {
                                      ...newAdmission.template_data,
                                      [field.id]: current,
                                    },
                                  });
                                }}
                              />
                              {opt}
                            </label>
                          );
                        })}
                      </div>
                    )}
                    {field.type === "eva" && (
                      <div
                        style={{
                          marginTop: "0.5rem",
                          marginBottom: "1rem",
                          background: "rgba(15, 23, 42, 0.4)",
                          padding: "1rem",
                          borderRadius: "0.5rem",
                          border: "1px solid rgba(255,255,255,0.1)",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginBottom: "0.5rem",
                            alignItems: "center",
                          }}
                        >
                          <span
                            style={{ fontSize: "0.85rem", color: "#94a3b8" }}
                          >
                            0 (Sin Dolor)
                          </span>
                          <span
                            style={{
                              fontWeight: "bold",
                              color: "#f8fafc",
                              fontSize: "1.2rem",
                              background: "rgba(255,255,255,0.1)",
                              padding: "0.2rem 0.8rem",
                              borderRadius: "0.25rem",
                            }}
                          >
                            {newAdmission.template_data[field.id] || 0}
                          </span>
                          <span
                            style={{ fontSize: "0.85rem", color: "#94a3b8" }}
                          >
                            10 (Máximo Dolor)
                          </span>
                        </div>
                        <ChromaticSlider
                          type="range"
                          min="0"
                          max="10"
                          value={newAdmission.template_data[field.id] || 0}
                          onChange={(e) =>
                            setNewAdmission({
                              ...newAdmission,
                              template_data: {
                                ...newAdmission.template_data,
                                [field.id]: Number(e.target.value),
                              },
                            })
                          }
                          style={{ margin: 0 }}
                        />
                      </div>
                    )}
                  </div>
                ))}
            </div>
          )}

          <div
            style={{
              marginTop: "2rem",
              display: "flex",
              justifyContent: "flex-end",
              gap: "1rem",
            }}
          >
            <button
              onClick={() => setIsAdmitting(false)}
              style={{
                padding: "0.75rem 1.5rem",
                background: "#CBD5E0",
                border: "none",
                borderRadius: "0.5rem",
                cursor: "pointer",
              }}
            >
              Cancelar
            </button>
            <button
              onClick={handleCreateAdmission}
              disabled={!newAdmission.template_id}
              style={{
                padding: "0.75rem 1.5rem",
                background: newAdmission.template_id ? "#319795" : "#4a5568",
                color: "white",
                border: "none",
                borderRadius: "0.5rem",
                cursor: newAdmission.template_id ? "pointer" : "not-allowed",
                fontWeight: "bold",
              }}
            >
              Guardar Admisión
            </button>
          </div>
        </Card>
      </Container>
    );
  }

  // --- DASHBOARD VIEW ---
  return (
    <Container>
      {ProfileHeaderContent}

      <Grid>
        {/* 2. Pharmacology Card (Ocultado a petición del usuario) */}
        {/*
        <Card color="#805AD5">
          <CardTitle>
            <Pill size={16} /> Farmacología Actual
          </CardTitle>
          {admission.medications && admission.medications.length > 0 ? (
            <ul>
              {admission.medications.map((med: any, i: number) => (
                <li key={i}>
                  {med.name} - {med.dose}
                </li>
              ))}
            </ul>
          ) : (
            <div
              style={{
                textAlign: "center",
                padding: "1rem",
                color: "#A0AEC0",
                border: "1px dashed #CBD5E0",
                borderRadius: "0.5rem",
              }}
            >
              Sin medicación registrada
              <br />
              <small>No hay interacciones detectadas</small>
            </div>
          )}
        </Card>
        */}

        {/* 3. Evolution / Progress Highlight */}
        <Card color="#48BB78">
          <CardTitle>
            <TrendingUp size={18} className="text-emerald-400" /> Última Evolución
          </CardTitle>

          {evolutions.length === 0 ? (
            <p style={{ color: "#94a3b8", fontStyle: "italic" }}>
              No hay evoluciones registradas.
            </p>
          ) : (
            <div>
              {/* Latest Evolution Highlight */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1rem" }}>
                <p
                  style={{
                    fontSize: "0.9rem",
                    color: "#94a3b8",
                    margin: 0,
                  }}
                >
                  Última visita: {evolutions[0].date}
                </p>

                {/* 6-Month Mandatory Follow-up Tracker */}
                {(() => {
                  // Ensure we parse the date correctly by forcing local time at noon
                  const lastDate = new Date(`${evolutions[0].date}T12:00:00`);
                  const followUpMonths = evolutions[0].next_follow_up_months || 6;
                  const nextDate = addMonths(lastDate, followUpMonths);
                  const daysLeft = differenceInDays(nextDate, new Date());
                  const isUrgent = daysLeft <= 30;

                  return (
                    <div style={{
                      padding: "0.5rem 0.75rem",
                      background: isUrgent ? "rgba(239, 68, 68, 0.15)" : "rgba(15, 23, 42, 0.4)",
                      border: `1px solid ${isUrgent ? "rgba(239, 68, 68, 0.4)" : "rgba(255, 255, 255, 0.1)"}`,
                      borderRadius: "0.5rem",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.75rem",
                      textAlign: "right"
                    }}>
                      <div style={{ fontSize: "1.2rem", color: isUrgent ? "#ef4444" : "#38bdf8" }}>
                        {isUrgent ? "⚠️" : "📅"}
                      </div>
                      <div style={{ textAlign: "left" }}>
                        <div style={{ color: isUrgent ? "#ef4444" : "#cbd5e1", fontWeight: "bold", fontSize: "0.85rem" }}>
                          {isUrgent ? "Seguimiento Legal Pendiente" : "Próximo Seguimiento Legal"}
                        </div>
                        <div style={{ color: isUrgent ? "#fca5a5" : "#94a3b8", fontSize: "0.75rem", marginTop: "0.1rem" }}>
                          {nextDate.toLocaleDateString("es-AR")}
                          <span style={{ fontWeight: "bold", marginLeft: "0.25rem" }}>
                            ({daysLeft > 0 ? `Faltan ${daysLeft} días` : `Vencido hace ${Math.abs(daysLeft)} días`})
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
              <div
                style={{ display: "flex", alignItems: "center", gap: "1rem" }}
              >
                <div
                  style={{
                    flex: 1,
                    padding: "1rem",
                    background: "rgba(74, 222, 128, 0.1)",
                    borderRadius: "0.5rem",
                    border: "1px solid rgba(74, 222, 128, 0.2)",
                  }}
                >
                  <small style={{ color: "#4ade80", fontWeight: "bold" }}>
                    MEJORÍA GLOBAL
                  </small>
                  <div
                    style={{
                      fontSize: "1.5rem",
                      fontWeight: "800",
                      color: "#f8fafc",
                    }}
                  >
                    {evolutions[0].improvement_percent > 0 ? "+" : ""}
                    {evolutions[0].improvement_percent || 0}%
                  </div>
                </div>
                <div
                  style={{
                    flex: 1,
                    padding: "1rem",
                    background: "rgba(30, 41, 59, 0.5)",
                    border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: "0.5rem",
                  }}
                >
                  <small style={{ color: "#94a3b8", fontWeight: "bold" }}>
                    DOLOR ACTUAL (EVA)
                  </small>
                  <div
                    style={{
                      fontSize: "1.5rem",
                      fontWeight: "800",
                      color: "#f8fafc",
                    }}
                  >
                    {evolutions[0].eva_score}{" "}
                    <span
                      style={{
                        fontSize: "0.8rem",
                        fontWeight: "normal",
                        color: "#64748b",
                      }}
                    >
                      / 10
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Card>
      </Grid>

      {/* 4. Full Clinical History Container */}
      <Card
        color="#3182ce"
        style={{ marginTop: "1.5rem", width: "100%", maxWidth: "none" }}
      >
        <CardTitle
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <span className="flex items-center gap-2">
            <History size={18} className="text-sky-400" /> Historial Clínico
          </span>
          <Button
            size="sm"
            variant="default"
            onClick={() => {
              const roleNeedsSignature = user?.role === "medico";
              const missingSignature = roleNeedsSignature && !user?.professional_signature_url;

              if (missingSignature) {
                Swal.fire({
                  title: 'Firma Requerida',
                  html: 'Por requerimientos legales, debes cargar tu <b>Firma Profesional</b> en la sección <b>Mi Cuenta</b> para registrar nuevas evoluciones clínicas.',
                  icon: 'warning',
                  iconColor: '#ef4444',
                  background: 'rgba(30, 41, 59, 0.95)',
                  color: '#f8fafc',
                  confirmButtonColor: '#3b82f6',
                  confirmButtonText: 'Entendido',
                  customClass: {
                    popup: 'glass-modal border border-white/10 rounded-xl',
                  }
                });
                return;
              }

              setIsEvolutionModalOpen(true);
            }}
            className="flex items-center gap-1.5"
          >
            <Plus size={15} /> Nueva Evolución / Acción
          </Button>
        </CardTitle>

        {evolutions.length === 0 ? (
          <p style={{ color: "#94a3b8", fontStyle: "italic", padding: "1rem" }}>
            No hay acciones ni evoluciones en el historial.
          </p>
        ) : (
          <div
            style={{
              maxHeight: "600px",
              overflowY: "auto",
              paddingRight: "0.5rem",
            }}
          >
            {evolutions.map((evo, idx) => {
              const isExpanded = expandedEvos.includes(idx);
              // Format HH:mm from created_at if available
              const timeString = evo.created_at
                ? new Date(evo.created_at).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })
                : "";

              // Resolve audit information (columns or resilient fallback)
              const lastModifiedAt = evo.updated_at || evo.template_data?._audit?.updated_at;
              const lastModifiedBy = evo.updated_by || evo.template_data?._audit?.updated_by;
              const canEdit = user?.role === "medico" || user?.role === "admin" || user?.role === "super_admin" || user?.role === "owner" || !user?.role;

              return (
                <div
                  key={evo.id || idx}
                  style={{
                    padding: "1rem",
                    borderBottom: "1px solid rgba(255, 255, 255, 0.05)",
                    background:
                      idx % 2 === 0
                        ? "rgba(255, 255, 255, 0.02)"
                        : "transparent",
                    transition: "all 0.2s ease",
                  }}
                >
                  {/* Compact Row Header (Clickable) */}
                  <div
                    onClick={() =>
                      setExpandedEvos((prev) =>
                        isExpanded
                          ? prev.filter((i) => i !== idx)
                          : [...prev, idx],
                      )
                    }
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      cursor: "pointer",
                      paddingBottom: isExpanded ? "0.75rem" : "0",
                      gap: "0.75rem",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.85rem",
                        flexWrap: "wrap",
                      }}
                    >
                      <div
                        style={{
                          fontWeight: "bold",
                          color: "#e2e8f0",
                          fontSize: "1.05rem",
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                        }}
                      >
                        {evo.date}
                        {timeString && (
                          <span
                            style={{
                              fontSize: "0.85rem",
                              color: "#94a3b8",
                              fontWeight: "normal",
                            }}
                          >
                            {timeString}
                          </span>
                        )}
                        {evo.title && (
                          <span
                            style={{
                              marginLeft: "0.25rem",
                              paddingLeft: "0.5rem",
                              borderLeft: "2px solid rgba(255, 255, 255, 0.2)",
                              color: "#f8fafc",
                            }}
                          >
                            {evo.title}
                          </span>
                        )}
                      </div>

                      <div
                        style={{
                          color:
                            evo.improvement_percent >= 0
                              ? "#4ade80"
                              : "#f87171",
                          fontWeight: "bold",
                          fontSize: "0.85rem",
                          background:
                            evo.improvement_percent >= 0
                              ? "rgba(74, 222, 128, 0.1)"
                              : "rgba(248, 113, 113, 0.1)",
                          padding: "0.2rem 0.5rem",
                          borderRadius: "0.25rem",
                        }}
                      >
                        {evo.improvement_percent > 0 ? "+" : ""}
                        {evo.improvement_percent}%
                      </div>

                      {/* Header Audit Badge */}
                      {lastModifiedAt && (
                        <span
                          title={`Última modificación: ${new Date(lastModifiedAt).toLocaleDateString("es-AR")} ${new Date(lastModifiedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} por ${lastModifiedBy || "Profesional"}`}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "0.3rem",
                            fontSize: "0.75rem",
                            color: "#f59e0b",
                            background: "rgba(245, 158, 11, 0.12)",
                            border: "1px solid rgba(245, 158, 11, 0.3)",
                            padding: "0.15rem 0.5rem",
                            borderRadius: "0.25rem",
                            fontWeight: 500,
                          }}
                        >
                          <Clock size={11} /> Modificado
                        </span>
                      )}
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                      {canEdit && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingEvolution(evo);
                          }}
                          className="h-7 px-2 text-xs text-slate-400 hover:text-sky-300 hover:bg-sky-500/10 border border-transparent hover:border-sky-500/20"
                          title="Editar y agregar detalles a la evolución"
                        >
                          <Pencil size={12} className="mr-1" /> Editar
                        </Button>
                      )}
                      <div
                        style={{
                          color: "#64748b",
                          transition: "transform 0.2s ease",
                          transform: isExpanded
                            ? "rotate(180deg)"
                            : "rotate(0deg)",
                        }}
                      >
                        <ChevronDown size={16} />
                      </div>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div
                      style={{
                        borderTop: "1px solid rgba(255, 255, 255, 0.1)",
                        paddingTop: "1rem",
                        marginTop: "0.25rem",
                        animation: "fadeIn 0.3s ease",
                      }}
                    >
                      {/* Prominent Audit Trail Banner */}
                      {lastModifiedAt && (
                        <div
                          style={{
                            marginBottom: "1rem",
                            padding: "0.6rem 0.85rem",
                            borderRadius: "0.5rem",
                            background:
                              "linear-gradient(90deg, rgba(245, 158, 11, 0.12) 0%, rgba(30, 41, 59, 0.6) 100%)",
                            border: "1px solid rgba(245, 158, 11, 0.3)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            flexWrap: "wrap",
                            gap: "0.5rem",
                            fontSize: "0.82rem",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "0.5rem",
                              color: "#fef3c7",
                            }}
                          >
                            <Clock size={15} className="text-amber-400 flex-shrink-0" />
                            <span>
                              <strong>Última modificación:</strong>{" "}
                              {new Date(lastModifiedAt).toLocaleDateString("es-AR")}{" "}
                              {new Date(lastModifiedAt).toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}{" "}
                              hs por{" "}
                              <strong style={{ color: "#ffffff" }}>
                                {lastModifiedBy || "Profesional actuante"}
                              </strong>
                            </span>
                          </div>
                          <span
                            style={{
                              fontSize: "0.72rem",
                              color: "#fbbf24",
                              textTransform: "uppercase",
                              letterSpacing: "0.05em",
                              fontWeight: 600,
                              background: "rgba(245, 158, 11, 0.15)",
                              padding: "0.15rem 0.45rem",
                              borderRadius: "0.25rem",
                            }}
                          >
                            Legajo Auditado
                          </span>
                        </div>
                      )}

                      <div
                        style={{
                          fontSize: "0.95rem",
                          color: "#cbd5e1",
                          marginBottom: "1rem",
                        }}
                      >
                        <strong style={{ color: "#e2e8f0" }}>EVA:</strong>{" "}
                        {evo.eva_score}{" "}
                        {evo.notes && (
                          <span
                            style={{ marginLeft: "0.5rem", color: "#94a3b8" }}
                          >
                            - {evo.notes}
                          </span>
                        )}
                      </div>

                      {evo.template_data &&
                        Object.keys(evo.template_data).length > 0 && (
                          <div
                            style={{
                              fontSize: "0.9rem",
                              background: "rgba(15, 23, 42, 0.6)",
                              padding: "1rem",
                              borderRadius: "0.5rem",
                              color: "#e2e8f0",
                              border: "1px solid rgba(255, 255, 255, 0.05)",
                              boxShadow: "inset 0 2px 4px 0 rgba(0, 0, 0, 0.1)",
                            }}
                          >
                            {Object.entries(evo.template_data).map(
                              ([key, val]) => {
                                const template = templates.find(
                                  (t: any) => t.id === evo.template_id,
                                );
                                const field = template?.fields?.find(
                                  (f: any) => f.id === key,
                                );
                                const label = field ? field.label : key;
                                const displayVal = Array.isArray(val)
                                  ? val.join(", ")
                                  : val;
                                if (!displayVal) return null;
                                return (
                                  <div
                                    key={key}
                                    style={{
                                      marginBottom: "0.6rem",
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: "0.2rem",
                                    }}
                                  >
                                    <strong
                                      style={{
                                        color: "#94a3b8",
                                        fontSize: "0.8rem",
                                        textTransform: "uppercase",
                                        letterSpacing: "0.05em",
                                      }}
                                    >
                                      {label}
                                    </strong>
                                    <span
                                      style={{
                                        color: "#f8fafc",
                                        fontSize: "1rem",
                                      }}
                                    >
                                      {String(displayVal)}
                                    </span>
                                  </div>
                                );
                              },
                            )}
                          </div>
                        )}

                      {evo.attachments && evo.attachments.length > 0 && (
                        <div
                          style={{
                            marginTop: "1rem",
                            background: "rgba(15, 23, 42, 0.4)",
                            padding: "1rem",
                            borderRadius: "0.5rem",
                            border: "1px solid rgba(255, 255, 255, 0.05)",
                          }}
                        >
                          <strong
                            style={{
                              color: "#cbd5e1",
                              fontSize: "0.9rem",
                              display: "flex",
                              alignItems: "center",
                              gap: "0.5rem",
                              marginBottom: "0.75rem",
                            }}
                          >
                            <Paperclip size={14} className="text-sky-400" /> Archivos Adjuntos (
                            {evo.attachments.length})
                          </strong>
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns:
                                "repeat(auto-fill, minmax(200px, 1fr))",
                              gap: "0.5rem",
                            }}
                          >
                            {evo.attachments.map((url: string, i: number) => {
                              const fileName =
                                url
                                  .split("/")
                                  .pop()
                                  ?.split("_")
                                  .slice(1)
                                  .join("_") || `Archivo adjunto ${i + 1}`;
                              return (
                                <a
                                  key={i}
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    background: "rgba(30, 41, 59, 0.6)",
                                    padding: "0.5rem 0.75rem",
                                    borderRadius: "0.375rem",
                                    border:
                                      "1px solid rgba(255, 255, 255, 0.1)",
                                    color: "#38bdf8",
                                    textDecoration: "none",
                                    fontSize: "0.8rem",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "0.5rem",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  <Paperclip size={13} />
                                  <span
                                    style={{
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    }}
                                  >
                                    {fileName}
                                  </span>
                                </a>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* AI Transcript Section */}
                      <div style={{ marginTop: "1rem" }}>
                        {/* 1. Has audio but NO transcript yet */}
                        {evo.audio_url && (!evo.ai_transcript || Object.keys(evo.ai_transcript).length === 0) && (
                          <div style={{
                            background: "rgba(139, 92, 246, 0.1)",
                            border: "1px dashed rgba(139, 92, 246, 0.3)",
                            padding: "1rem",
                            borderRadius: "0.5rem",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}>
                            <div>
                              <h4 style={{ color: "#c4b5fd", margin: "0 0 0.25rem 0", fontSize: "0.95rem", display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                <Mic size={15} className="text-violet-400" /> Audio Pendiente de Procesar
                              </h4>
                              <p style={{ color: "#a78bfa", fontSize: "0.85rem", margin: 0 }}>
                                Existe una grabación adjunta a esta evolución.
                              </p>
                            </div>
                            <Button
                              type="button"
                              size="sm"
                              variant="default"
                              onClick={() => handleTranscribeAudio(evo.id, evo.audio_url)}
                              disabled={transcribingEvoId === evo.id}
                              className="flex items-center gap-1.5 bg-violet-600 hover:bg-violet-500 border-violet-500"
                            >
                              {transcribingEvoId === evo.id ? (
                                <><Loader2 size={14} className="animate-spin" /> Analizando...</>
                              ) : (
                                <><Sparkles size={14} /> Extraer Legajo con IA</>
                              )}
                            </Button>
                          </div>
                        )}

                        {/* 2. Has AI Transcript Results */}
                        {evo.ai_transcript && Object.keys(evo.ai_transcript).length > 0 && (
                          <div style={{
                            background: "linear-gradient(145deg, rgba(88, 28, 135, 0.1) 0%, rgba(15, 23, 42, 0.4) 100%)",
                            border: "1px solid rgba(139, 92, 246, 0.3)",
                            borderRadius: "0.75rem",
                            padding: "1.25rem",
                            position: "relative",
                            overflow: "hidden"
                          }}>
                            <div style={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              height: "4px",
                              width: "100%",
                              background: "linear-gradient(90deg, #8b5cf6 0%, #d946ef 100%)"
                            }} />

                            <h4 style={{ color: "#d8b4fe", display: "flex", alignItems: "center", gap: "0.5rem", marginTop: 0, marginBottom: "1rem", fontSize: "1.05rem" }}>
                              <Sparkles size={16} className="text-purple-400" /> Análisis Clínico IA
                            </h4>

                            {evo.ai_transcript.resumen_motivo_consulta && (
                              <div style={{ marginBottom: "1rem" }}>
                                <strong style={{ color: "#c4b5fd", fontSize: "0.85rem", textTransform: "uppercase" }}>Motivo / Resumen</strong>
                                <p style={{ color: "#f8fafc", margin: "0.25rem 0 0 0", fontSize: "0.95rem", lineHeight: "1.5" }}>
                                  {evo.ai_transcript.resumen_motivo_consulta}
                                </p>
                              </div>
                            )}

                            {evo.ai_transcript.sintomas && evo.ai_transcript.sintomas.length > 0 && (
                              <div style={{ marginBottom: "1rem" }}>
                                <strong style={{ color: "#c4b5fd", fontSize: "0.85rem", textTransform: "uppercase" }}>Síntomas Identificados</strong>
                                <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginTop: "0.5rem" }}>
                                  {evo.ai_transcript.sintomas.map((s: string, idx: number) => (
                                    <span key={idx} style={{
                                      background: "rgba(139, 92, 246, 0.2)",
                                      color: "#ddd6fe",
                                      padding: "0.2rem 0.6rem",
                                      borderRadius: "1rem",
                                      fontSize: "0.8rem",
                                      border: "1px solid rgba(139, 92, 246, 0.3)"
                                    }}>
                                      {s}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}

                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
                              {evo.ai_transcript.observaciones_clinicas && (
                                <div>
                                  <strong style={{ color: "#c4b5fd", fontSize: "0.85rem", textTransform: "uppercase" }}>Observaciones</strong>
                                  <p style={{ color: "#cbd5e1", margin: "0.25rem 0 0 0", fontSize: "0.9rem" }}>{evo.ai_transcript.observaciones_clinicas}</p>
                                </div>
                              )}
                              {evo.ai_transcript.recomendaciones_tratamiento && (
                                <div>
                                  <strong style={{ color: "#c4b5fd", fontSize: "0.85rem", textTransform: "uppercase" }}>Recomendaciones</strong>
                                  <p style={{ color: "#cbd5e1", margin: "0.25rem 0 0 0", fontSize: "0.9rem" }}>{evo.ai_transcript.recomendaciones_tratamiento}</p>
                                </div>
                              )}
                            </div>

                            <div style={{ marginTop: "1rem", paddingTop: "0.75rem", borderTop: "1px solid rgba(139, 92, 246, 0.2)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                              {evo.ai_transcript.nivel_dolor_eva_mencionado !== null && evo.ai_transcript.nivel_dolor_eva_mencionado !== undefined ? (
                                <span style={{ color: "#e2e8f0", fontSize: "0.85rem" }}>
                                  <strong>Dolor Relatado (EVA):</strong> <span style={{ color: "#fbbf24", fontWeight: "bold" }}>{evo.ai_transcript.nivel_dolor_eva_mencionado}/10</span>
                                </span>
                              ) : <span />}

                              {evo.audio_url && (
                                <a
                                  href={`${supabase.storage.from("ai_clinical_audio").getPublicUrl(evo.audio_url.replace('ai_clinical_audio/', '')).data.publicUrl}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    fontSize: "0.8rem",
                                    color: "#8b5cf6",
                                    textDecoration: "underline",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "0.3rem"
                                  }}
                                >
                                  <Mic size={13} /> Escuchar Audio Original
                                </a>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* EVOLUTION MODAL */}
      {isEvolutionModalOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <Card
            style={{
              width: "100%",
              maxWidth: "500px",
              maxHeight: "90vh",
              overflowY: "auto",
            }}
          >
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleAddEvolution();
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "1rem",
                  flexWrap: "wrap",
                  gap: "0.5rem",
                }}
              >
                <CardTitle style={{ margin: 0 }}>Registrar Evolución</CardTitle>
                {draftSavedAt && (
                  <span
                    style={{
                      fontSize: "0.75rem",
                      color: "#34d399",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.35rem",
                      background: "rgba(16, 185, 129, 0.12)",
                      border: "1px solid rgba(16, 185, 129, 0.3)",
                      padding: "0.25rem 0.65rem",
                      borderRadius: "9999px",
                    }}
                  >
                    <span
                      style={{
                        display: "inline-block",
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        background: "#10b981",
                        boxShadow: "0 0 6px #10b981",
                      }}
                    />
                    Borrador auto-guardado
                  </span>
                )}
              </div>

              {draftRestoredAt && (
                <div
                  style={{
                    background: "rgba(16, 185, 129, 0.12)",
                    border: "1px solid rgba(16, 185, 129, 0.35)",
                    borderRadius: "0.6rem",
                    padding: "0.75rem 1rem",
                    marginBottom: "1.25rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "0.75rem",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      fontSize: "0.85rem",
                      color: "#6ee7b7",
                    }}
                  >
                    <span>🛡️ <b>Borrador recuperado:</b> Se restauraron tus notas no guardadas de esta consulta.</span>
                  </div>
                  <button
                    type="button"
                    onClick={handleDiscardDraft}
                    style={{
                      background: "rgba(239, 68, 68, 0.15)",
                      border: "1px solid rgba(239, 68, 68, 0.4)",
                      color: "#fca5a5",
                      fontSize: "0.75rem",
                      padding: "0.3rem 0.6rem",
                      borderRadius: "0.375rem",
                      cursor: "pointer",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Descartar borrador
                  </button>
                </div>
              )}

              {/* Title Section (New) */}
              <div style={{ marginBottom: "1.5rem" }}>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    marginBottom: "0.5rem",
                    fontWeight: "bold",
                    color: "#f8fafc",
                  }}
                >
                  <TrendingUp size={16} className="text-emerald-400" /> Título de la Evolución / Resumen
                </label>
                <input
                  type="text"
                  placeholder="Ej: Seguimiento Mensual, Dolor Cervical Agudo..."
                  style={{
                    width: "100%",
                    padding: "0.75rem",
                    borderRadius: "0.5rem",
                    border: "1px solid rgba(255,255,255,0.2)",
                    fontFamily: "inherit",
                    background: "rgba(15, 23, 42, 0.5)",
                    color: "white",
                  }}
                  value={newEvolution.title}
                  onChange={(e) =>
                    setNewEvolution({ ...newEvolution, title: e.target.value })
                  }
                />
              </div>

              {/* Date Section */}
              <div style={{ marginBottom: "1.5rem" }}>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    marginBottom: "0.5rem",
                    fontWeight: "bold",
                    color: "#f8fafc",
                  }}
                >
                  <Clock size={16} className="text-sky-400" /> Fecha del Registro / Estudio Previos
                </label>
                <input
                  type="date"
                  max={new Date().toISOString().split("T")[0]}
                  style={{
                    width: "100%",
                    padding: "0.75rem",
                    borderRadius: "0.5rem",
                    border: "1px solid rgba(255,255,255,0.2)",
                    fontFamily: "inherit",
                    background: "rgba(15, 23, 42, 0.5)",
                    color: "white",
                    colorScheme: "dark"
                  }}
                  value={newEvolution.date}
                  onChange={(e) =>
                    setNewEvolution({ ...newEvolution, date: e.target.value })
                  }
                  required
                />
              </div>

              {/* AI Clinical Audio Recording Section */}
              <AudioRecorderWidget
                orgId={selectedOrgId || ""}
                patientId={id || ""}
                onAudioRecorded={(url) => setNewEvolution((prev) => ({ ...prev, audio_url: url }))}
              />

              {/* Follow-up Timeline Section (New) */}
              <div style={{ marginBottom: "1.5rem" }}>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    marginBottom: "0.5rem",
                    fontWeight: "bold",
                    color: "#f8fafc",
                  }}
                >
                  <Clock size={16} className="text-amber-400" /> Próximo Seguimiento (en Plazo Legal)
                </label>
                <CustomSelect
                  options={[
                    { value: "1", label: "1 Mes" },
                    { value: "3", label: "3 Meses" },
                    { value: "6", label: "6 Meses" },
                    { value: "12", label: "12 Meses" },
                  ]}
                  value={String(newEvolution.next_follow_up_months)}
                  onChange={(value) =>
                    setNewEvolution({ ...newEvolution, next_follow_up_months: Number(value) })
                  }
                  placeholder="Seleccionar plazo..."
                />
                <small style={{ color: "#94a3b8", display: "block", marginTop: "0.25rem" }}>
                  Altera cuándo se activará la alarma visual obligando al paciente a un nuevo chequeo clínico.
                </small>
              </div>

              {/* Template Selector Section */}
              <div
                style={{
                  background: "rgba(30, 41, 59, 0.4)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  padding: "1rem",
                  borderRadius: "0.5rem",
                  marginBottom: "1.5rem",
                }}
              >
                <label
                  style={{
                    marginBottom: "0.75rem",
                    fontWeight: "bold",
                    color: "#f8fafc",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                  }}
                >
                  <ClipboardList size={16} className="text-emerald-400" /> Utilizar Plantilla Clínica
                </label>
                <CustomSelect
                  options={[
                    { value: "", label: "No usar plantilla (Evolución Libre)" },
                    ...templates.map((t) => ({ value: t.id, label: t.name })),
                  ]}
                  value={newEvolution.template_id || ""}
                  onChange={(value) => {
                    if (!value) {
                      setNewEvolution({
                        ...newEvolution,
                        template_id: null,
                        template_data: {},
                      });
                      return;
                    }
                    const selected = templates.find((t) => t.id === value);
                    const defaultData: Record<string, any> = {};
                    selected?.fields.forEach((f: any) => {
                      if (f.type === "checkbox") defaultData[f.id] = [];
                      else defaultData[f.id] = "";
                    });
                    setNewEvolution({
                      ...newEvolution,
                      template_id: value,
                      template_data: defaultData,
                    });
                  }}
                  placeholder="Seleccione una plantilla"
                />
              </div>

              {/* Dynamics fields block */}
              {newEvolution.template_id && (
                <div
                  style={{
                    background: "rgba(30, 41, 59, 0.4)",
                    padding: "1rem",
                    borderRadius: "0.5rem",
                    marginBottom: "1.5rem",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                  }}
                >
                  <h4 style={{ margin: "0 0 1rem 0", color: "#f8fafc" }}>
                    Datos de la Plantilla
                  </h4>
                  {templates
                    .find((t) => t.id === newEvolution.template_id)
                    ?.fields.map((field: any) => (
                      <div key={field.id} style={{ marginBottom: "1rem" }}>
                        <label
                          style={{
                            display: "block",
                            marginBottom: "0.5rem",
                            fontWeight: "600",
                            color: "#cbd5e1",
                            fontSize: "0.9rem",
                          }}
                        >
                          {field.label}{" "}
                          {field.required && (
                            <span style={{ color: "#ef4444" }}>*</span>
                          )}
                        </label>

                        {field.type === "text" && (
                          <input
                            type="text"
                            style={{
                              width: "100%",
                              padding: "0.6rem",
                              borderRadius: "0.375rem",
                              border: "1px solid rgba(255,255,255,0.2)",
                              background: "rgba(15, 23, 42, 0.5)",
                              color: "white",
                            }}
                            value={newEvolution.template_data[field.id] || ""}
                            onChange={(e) =>
                              setNewEvolution({
                                ...newEvolution,
                                template_data: {
                                  ...newEvolution.template_data,
                                  [field.id]: e.target.value,
                                },
                              })
                            }
                            required={field.required}
                          />
                        )}
                        {field.type === "textarea" && (
                          <textarea
                            style={{
                              width: "100%",
                              padding: "0.6rem",
                              borderRadius: "0.375rem",
                              border: "1px solid rgba(255,255,255,0.2)",
                              background: "rgba(15, 23, 42, 0.5)",
                              color: "white",
                              minHeight: "80px",
                            }}
                            value={newEvolution.template_data[field.id] || ""}
                            onChange={(e) =>
                              setNewEvolution({
                                ...newEvolution,
                                template_data: {
                                  ...newEvolution.template_data,
                                  [field.id]: e.target.value,
                                },
                              })
                            }
                            required={field.required}
                          />
                        )}
                        {field.type === "date" && (
                          <input
                            type="date"
                            style={{
                              width: "100%",
                              padding: "0.6rem",
                              borderRadius: "0.375rem",
                              border: "1px solid rgba(255,255,255,0.2)",
                              background: "rgba(15, 23, 42, 0.5)",
                              color: "white",
                            }}
                            value={newEvolution.template_data[field.id] || ""}
                            onChange={(e) =>
                              setNewEvolution({
                                ...newEvolution,
                                template_data: {
                                  ...newEvolution.template_data,
                                  [field.id]: e.target.value,
                                },
                              })
                            }
                            required={field.required}
                          />
                        )}
                        {field.type === "select" && (
                          <select
                            style={{
                              width: "100%",
                              padding: "0.6rem",
                              borderRadius: "0.375rem",
                              border: "1px solid rgba(255,255,255,0.2)",
                              background: "rgba(15, 23, 42, 0.9)",
                              color: "white",
                            }}
                            value={newEvolution.template_data[field.id] || ""}
                            onChange={(e) =>
                              setNewEvolution({
                                ...newEvolution,
                                template_data: {
                                  ...newEvolution.template_data,
                                  [field.id]: e.target.value,
                                },
                              })
                            }
                            required={field.required}
                          >
                            <option value="">Seleccione...</option>
                            {field.options?.map((opt: string) => (
                              <option key={opt} value={opt}>
                                {opt}
                              </option>
                            ))}
                          </select>
                        )}
                        {field.type === "checkbox" && (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "0.5rem",
                              background: "rgba(15, 23, 42, 0.5)",
                              padding: "0.5rem",
                              borderRadius: "0.375rem",
                              border: "1px solid rgba(255,255,255,0.2)",
                              color: "lightgray",
                            }}
                          >
                            {field.options?.map((opt: string) => {
                              const isChecked = (
                                newEvolution.template_data[field.id] || []
                              ).includes(opt);
                              return (
                                <label
                                  key={opt}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "0.5rem",
                                    fontSize: "0.9rem",
                                    cursor: "pointer",
                                    margin: 0,
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={(e) => {
                                      const current = [
                                        ...(newEvolution.template_data[
                                          field.id
                                        ] || []),
                                      ];
                                      if (e.target.checked) current.push(opt);
                                      else {
                                        const idx = current.indexOf(opt);
                                        if (idx > -1) current.splice(idx, 1);
                                      }
                                      setNewEvolution({
                                        ...newEvolution,
                                        template_data: {
                                          ...newEvolution.template_data,
                                          [field.id]: current,
                                        },
                                      });
                                    }}
                                  />
                                  {opt}
                                </label>
                              );
                            })}
                          </div>
                        )}
                        {field.type === "eva" && (
                          <div
                            style={{
                              marginTop: "0.5rem",
                              marginBottom: "1rem",
                              background: "rgba(15, 23, 42, 0.4)",
                              padding: "1rem",
                              borderRadius: "0.5rem",
                              border: "1px solid rgba(255,255,255,0.1)",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                marginBottom: "0.5rem",
                                alignItems: "center",
                              }}
                            >
                              <span
                                style={{
                                  fontSize: "0.85rem",
                                  color: "#94a3b8",
                                }}
                              >
                                0 (Sin Dolor)
                              </span>
                              <span
                                style={{
                                  fontWeight: "bold",
                                  color: "#f8fafc",
                                  fontSize: "1.2rem",
                                  background: "rgba(255,255,255,0.1)",
                                  padding: "0.2rem 0.8rem",
                                  borderRadius: "0.25rem",
                                }}
                              >
                                {newEvolution.template_data[field.id] || 0}
                              </span>
                              <span
                                style={{
                                  fontSize: "0.85rem",
                                  color: "#94a3b8",
                                }}
                              >
                                10 (Máximo Dolor)
                              </span>
                            </div>
                            <ChromaticSlider
                              type="range"
                              min="0"
                              max="10"
                              value={newEvolution.template_data[field.id] || 0}
                              onChange={(e) =>
                                setNewEvolution({
                                  ...newEvolution,
                                  template_data: {
                                    ...newEvolution.template_data,
                                    [field.id]: Number(e.target.value),
                                  },
                                })
                              }
                              style={{ margin: 0 }}
                            />
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              )}

              {/* Native EVA Score (only if template doesn't provide one) */}
              {(!newEvolution.template_id ||
                !templates
                  .find((t) => t.id === newEvolution.template_id)
                  ?.fields?.some((f: any) => f.type === "eva")) && (
                  <div
                    style={{
                      background: "rgba(30, 41, 59, 0.4)",
                      padding: "1rem",
                      borderRadius: "0.5rem",
                      marginBottom: "1.5rem",
                      border: "1px solid rgba(255, 255, 255, 0.1)",
                    }}
                  >
                    <label
                      style={{
                        marginBottom: "1rem",
                        fontWeight: "bold",
                        color: "#f8fafc",
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                      }}
                    >
                      <TrendingUp size={16} className="text-emerald-400" /> Nivel de Dolor Actual (EVA)
                    </label>
                    <div
                      style={{
                        background: "rgba(15, 23, 42, 0.4)",
                        padding: "1rem",
                        borderRadius: "0.5rem",
                        border: "1px solid rgba(255,255,255,0.05)",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginBottom: "0.5rem",
                          alignItems: "center",
                        }}
                      >
                        <span style={{ fontSize: "0.85rem", color: "#94a3b8" }}>
                          0 (Sin Dolor)
                        </span>
                        <span
                          style={{
                            fontWeight: "bold",
                            color: "#f8fafc",
                            fontSize: "1.2rem",
                            background: "rgba(255,255,255,0.1)",
                            padding: "0.2rem 0.8rem",
                            borderRadius: "0.25rem",
                          }}
                        >
                          {newEvolution.eva_score}
                        </span>
                        <span style={{ fontSize: "0.85rem", color: "#94a3b8" }}>
                          10 (Máximo Dolor)
                        </span>
                      </div>
                      <ChromaticSlider
                        type="range"
                        min="0"
                        max="10"
                        value={newEvolution.eva_score}
                        onChange={(e) =>
                          setNewEvolution({
                            ...newEvolution,
                            eva_score: Number(e.target.value),
                          })
                        }
                        style={{ margin: 0 }}
                      />
                    </div>
                  </div>
                )}

              {/* File Upload Section */}
              <div
                style={{
                  marginTop: "1.5rem",
                  background: "rgba(30, 41, 59, 0.4)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  padding: "1rem",
                  borderRadius: "0.5rem",
                  marginBottom: "1.5rem",
                }}
              >
                <label
                  style={{
                    marginBottom: "1rem",
                    fontWeight: "bold",
                    color: "#f8fafc",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                  }}
                >
                  <Paperclip size={16} className="text-sky-400" /> Archivos Adjuntos (Estudios, Radiografías,
                  etc.)
                </label>

                <label
                  htmlFor="evolution-file-input"
                  onDragEnter={handleDrag}
                  onDragOver={handleDrag}
                  onDragLeave={handleDrag}
                  onDrop={handleDrop}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "1.5rem",
                    border: `2px dashed ${isDragActive ? "#38bdf8" : "rgba(255, 255, 255, 0.2)"}`,
                    borderRadius: "0.5rem",
                    background: isDragActive
                      ? "rgba(56, 189, 248, 0.1)"
                      : "rgba(15, 23, 42, 0.3)",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    color: isDragActive ? "#38bdf8" : "#94a3b8",
                    marginBottom: "1rem",
                  }}
                  onMouseEnter={(e) => {
                    if (!isDragActive) {
                      e.currentTarget.style.background = "rgba(15, 23, 42, 0.6)";
                      e.currentTarget.style.borderColor =
                        "rgba(56, 189, 248, 0.5)";
                      e.currentTarget.style.color = "#38bdf8";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isDragActive) {
                      e.currentTarget.style.background = "rgba(15, 23, 42, 0.3)";
                      e.currentTarget.style.borderColor =
                        "rgba(255, 255, 255, 0.2)";
                      e.currentTarget.style.color = "#94a3b8";
                    }
                  }}
                >
                  <Upload size={24} style={{ marginBottom: "0.5rem" }} />
                  <span>
                    {isDragActive
                      ? "Arrastra para soltar"
                      : "Click para elegir archivos o arrastra aquí"}
                  </span>
                </label>
                <input
                  id="evolution-file-input"
                  type="file"
                  multiple
                  onChange={(e) => {
                    if (e.target.files) {
                      setNewEvolution((prev) => ({
                        ...prev,
                        files: [
                          ...prev.files,
                          ...Array.from(e.target.files!),
                        ],
                      }));
                      e.target.value = ""; // Reset input to allow adding same file again if removed
                    }
                  }}
                  style={{ display: "none" }}
                />

                {newEvolution.files.length > 0 && (
                  <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                    {newEvolution.files.map((file, idx) => (
                      <li
                        key={idx}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          background: "rgba(15, 23, 42, 0.6)",
                          padding: "0.5rem",
                          borderRadius: "0.25rem",
                          marginBottom: "0.5rem",
                          border: "1px solid rgba(255, 255, 255, 0.05)",
                        }}
                      >
                        <span
                          style={{
                            fontSize: "0.85rem",
                            color: "#cbd5e1",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: "85%",
                          }}
                        >
                          {file.name}
                        </span>
                        <button
                          type="button"
                          onClick={() =>
                            setNewEvolution((prev) => ({
                              ...prev,
                              files: prev.files.filter((_, i) => i !== idx),
                            }))
                          }
                          style={{
                            background: "transparent",
                            border: "none",
                            color: "#ef4444",
                            cursor: "pointer",
                            padding: "0.25rem",
                          }}
                          title="Eliminar archivo"
                        >
                          <Trash2 size={13} />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <label
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  fontWeight: "bold",
                  color: "#cbd5e1",
                }}
              >
                Notas de Evolución (Adicional)
              </label>
              <textarea
                style={{
                  width: "100%",
                  height: "100px",
                  padding: "0.75rem",
                  borderRadius: "0.5rem",
                  border: "1px solid rgba(255,255,255,0.2)",
                  fontFamily: "inherit",
                  background: "rgba(15, 23, 42, 0.5)",
                  color: "white",
                }}
                placeholder="Notas libres de evolución del paciente..."
                value={newEvolution.notes}
                onChange={(e) =>
                  setNewEvolution({ ...newEvolution, notes: e.target.value })
                }
              />

              <div
                style={{
                  marginTop: "2rem",
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "1rem",
                }}
              >
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsEvolutionModalOpen(false)}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="default"
                  isLoading={isUploadingEvolution}
                  disabled={isUploadingEvolution}
                >
                  {isUploadingEvolution ? "Subiendo archivos..." : "Guardar"}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}

      {/* EDIT EVOLUTION MODAL */}
      <EditEvolutionModal
        isOpen={!!editingEvolution}
        onClose={() => setEditingEvolution(null)}
        evolution={editingEvolution}
        templates={templates}
        onSave={handleSaveEditedEvolution}
        isSaving={isSavingEdit}
      />
    </Container>
  );
};


export default PatientDetail;
